import { ReactNode } from "react";
import { Navigate, useLocation, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

interface ProtectedRouteProps {
    children: ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
    const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
    const location = useLocation();

    // Use react-query to check config status, sharing cache with ConfigPage
    const { data: config, isLoading: isConfigLoading } = useQuery({
        queryKey: ["config"],
        queryFn: api.getConfig,
        enabled: isAuthenticated, // Only check if authenticated
        retry: false
    });

    const isPartial = (config as any)?.status === "partial";

    if (isAuthLoading) {
        return (
            <div className="w-full h-screen flex items-center justify-center">
                <div className="text-slate-600 font-semibold">Loading...</div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Prepare banner if partial
    // Note: We render children regardless of partial status to allow navigation
    return (
        <div className="flex flex-col h-screen overflow-hidden">
            {/* Global Warning Banner for Partial Config - Only show when loaded and strictly needed */}
            {!isConfigLoading && isPartial && location.pathname !== "/v1/config" && (
                <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between text-amber-800 animate-in slide-in-from-top duration-300 z-50">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">⚠️ Configuration is incomplete. Some features may not work.</span>
                    </div>

                    <Link to="/v1/config" className="text-xs font-bold bg-white/50 px-2 py-1 rounded hover:bg-white/80 transition-colors uppercase tracking-wide">
                        Finish Setup
                    </Link>
                </div>
            )}

            {/* 
               We wrap children in a flex-1 container to ensure they take remaining space.
               Since Layout usually has h-screen, we might need to adjust Layout or just accept 
               that this wrapper makes the 'screen' effectively smaller for the Layout.
               However, Layout has 'h-screen' hardcoded. To avoid double scrollbars, 
               we should ideally pass a 'bannerVisible' prop or context, BUT
               wrapping in a flex container with overflow-hidden should generally work 
               if Layout handles its own internal scrolling (which it does via flex-1 overflow-y-auto).
            */}
            <div className="flex-1 w-full h-full relative">
                {children}
            </div>
        </div>
    );
}
